import styled from '@emotion/styled'
import React, { useCallback } from 'react'
import { useMutation } from '../../../api/graphql'
import { Hugeicon } from '../../../icons/Hugeicon'
import { useChatContext } from './ChatProvider'
import { CONVERSATION_CLOSE_MUTATION } from './chatQueries'
import ConversationList from './ConversationList'
import ConversationView from './ConversationView'
import NewConversation from './NewConversation'

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
  z-index: 9998;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const Panel = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 380px;
  height: 560px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideUp 0.25s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.97);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media (max-width: 480px) {
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #102638;
  color: white;
  border-radius: 12px 12px 0 0;
  border-bottom: 2px solid #6cc14c;

  @media (max-width: 480px) {
    border-radius: 0;
  }
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  min-height: 28px;
`

const KiteIcon = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  margin-right: 10px;
  flex-shrink: 0;
`

const HeaderTitle = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 16px;
  font-weight: 600;
  color: white;
  line-height: 28px;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const IconButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`

const ResolveButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  color: white;
  cursor: pointer;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`

const BackButton = styled(IconButton)`
  margin-right: 4px;
`

const Content = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const ChatPanel: React.FC = () => {
  const { isOpen, activeConversationId, closeChat, goToList, showNewConversation } = useChatContext()
  const [closeConversation] = useMutation(CONVERSATION_CLOSE_MUTATION)

  const handleResolve = useCallback(async () => {
    if (!activeConversationId) return
    await closeConversation({ variables: { input: { conversationId: activeConversationId } } })
  }, [activeConversationId, closeConversation])

  if (!isOpen) return null

  const showBack = activeConversationId || showNewConversation
  const title = showNewConversation ? 'New Conversation' : activeConversationId ? 'Conversation' : 'Support'
  const showResolve = !!activeConversationId && !showNewConversation

  return (
    <>
      <Overlay onClick={closeChat} />
      <Panel>
        <Header>
          <HeaderLeft>
            {showBack && (
              <BackButton onClick={goToList} aria-label="Back to conversations">
                <Hugeicon name="arrow-left-long" size={20} />
              </BackButton>
            )}
            {!showBack && <KiteIcon src="/img/email-kite-logo.png" alt="" />}
            <HeaderTitle>{title}</HeaderTitle>
          </HeaderLeft>
          <HeaderActions>
            {showResolve && (
              <ResolveButton onClick={handleResolve} aria-label="Mark resolved">
                <Hugeicon name="tick" size={14} />
                Resolve
              </ResolveButton>
            )}
            <IconButton onClick={closeChat} aria-label="Close chat">
              <Hugeicon name="cancel" size={20} />
            </IconButton>
          </HeaderActions>
        </Header>
        <Content>
          {showNewConversation ? (
            <NewConversation />
          ) : activeConversationId ? (
            <ConversationView conversationId={activeConversationId} />
          ) : (
            <ConversationList />
          )}
        </Content>
      </Panel>
    </>
  )
}

export default ChatPanel
